import { BrandLoader } from '@/components/common/BrandLoader'
import type { ReactNode } from 'react'
import { CircleAlert, CircleCheck } from 'lucide-react'
import { Button } from '@teach/ui'
import type { CourseWithoutBlocks } from '@/types/course'
import type { ReviewRead, Visibility } from '@/features/moderation/types'
import { failedReviewText } from '@/features/moderation/lib/reviewText'
import { outcomeTexts } from '@/features/moderation/lib/reviewOutcome'
import { ViolationList } from './ViolationList'
import { ReviewingUpdate } from './ReviewingUpdate'
import type { ViolationGo } from './violationPlace'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  review: ReviewRead | null
  visibility: Visibility
  /** Черновик живого курса отличается от живой версии и не подан (контракт §2). */
  hasUnsubmittedChanges: boolean
  course: CourseWithoutBlocks
  onNavigate: () => void
  /** Свой переход к нарушению (см. `ViolationList`). */
  onGo?: (target: ViolationGo) => void
  /** Кнопки отправки прямо в карточке отказа — вместо подсказки «Исправьте…»:
   *  кнопка «Исправлено, обновить» говорит то же самое. */
  actions?: ReactNode
}

const box = 'flex items-start gap-2.5 rounded-2xl border p-4 text-sm'

/** Плашка состояния проверки над селектом доступа. */
export const ReviewStatusPanel = ({
  review,
  visibility,
  hasUnsubmittedChanges,
  course,
  onNavigate,
  onGo,
  actions,
}: Props) => {
  const m = useMessages(messages)
  // Любая проверка — в том же сером блоке, что и правки: это продолжение
  // действия автора, а не тревога. «Отозвать заявку» снята (решение заказчика).
  if (review?.status === 'pending') {
    return <ReviewingUpdate kind={review.kind} live={visibility !== 'unpublished'} />
  }
  // Обновление живого курса одобрено и новых правок после него нет — говорим об
  // этом зелёным там же, где раньше стояла кнопка «Обновить». Новая правка
  // (`hasUnsubmittedChanges`) плашку снимает: дальше снова «Есть изменения».
  if (
    review?.status === 'approved' &&
    review.kind === 'update' &&
    visibility !== 'unpublished' &&
    !hasUnsubmittedChanges
  ) {
    return (
      <div role="status" className={`${box} border-success/30 bg-success/10 text-success`}>
        <CircleCheck className="mt-0.5 h-5 w-5 shrink-0" />
        <div className="space-y-1">
          <p className="font-medium">{m.updateApprovedTitle}</p>
          <p className="text-muted-foreground">{m.updateApprovedBody}</p>
        </div>
      </div>
    )
  }
  const texts = outcomeTexts(review, visibility, hasUnsubmittedChanges)
  if (texts === null) return null
  if (review?.status === 'rejected') {
    // Одна карточка, а не три отдельных куска: заголовок отказа, нарушения и
    // подсказка читаются как один ответ проверки.
    return (
      <section className="overflow-hidden rounded-2xl border border-destructive/30 bg-destructive/10 text-sm">
        <div className="flex items-start gap-2.5 p-4 text-destructive">
          <CircleAlert className="mt-0.5 h-5 w-5 shrink-0" />
          <p className="font-medium">{texts.rejectedTitle}</p>
        </div>
        <div className="border-t border-destructive/20 px-2 py-2">
          <ViolationList
            course={course}
            violations={review.violations}
            onNavigate={onNavigate}
            onGo={onGo}
          />
        </div>
        {actions ? (
          <div className="border-t border-destructive/20 p-4">{actions}</div>
        ) : (
          <p className="border-t border-destructive/20 px-4 py-3 text-muted-foreground">
            {texts.rejectedHint}
          </p>
        )}
      </section>
    )
  }
  if (review?.status === 'failed') {
    return (
      <div className={`${box} border-warning/30 bg-warning/10 text-warning`}>
        <CircleAlert className="mt-0.5 h-5 w-5 shrink-0" />
        <div className="space-y-1">
          <p className="font-medium">{failedReviewText(review.errorCode)}</p>
          {texts.retryHint ? <p>{texts.retryHint}</p> : null}
        </div>
      </div>
    )
  }
  return null
}
